import {
  Modal,
  ModalOverlay,
  ModalContent,
  Text,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  SimpleGrid,
  Flex,
} from '@chakra-ui/react'
import { Image } from '@/components/HOC/Image'
import AirdropTestnet from '@/public/assets/images/lootbox/airdrop-testnet.png'
import AirdropSocial from '@/public/assets/images/lootbox/airdrop-social.png'
import AirdropNftHolders from '@/public/assets/images/lootbox/airdrop-holders.png'
import AirdropMainnet from '@/public/assets/images/lootbox/airdrop-mainnet.png'
import { useMutation } from '@tanstack/react-query'
import { useAppToast } from '@/hooks/useAppToast'
import { useState } from 'react'
import { useSession } from '@/context/SessionProvider'
import { useTranslation } from 'next-i18next/pages'
import ChestIcon from '@/public/assets/icons/chest.svg'
import { API_GATEWAY } from '@/constants/constants'

export const AirdropChecker = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation()

  return (
    <Modal size={'3xl'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('airdrop.title')}
        </ModalHeader>
        <ModalBody pb={5}>
          <ModalCloseButton onClick={onClose} size="sm" />
          <AirdropCheckerContent />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const AirdropCheckerContent = () => {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={{ base: 2, md: 6 }}>
      <AirdropTestnetCard />
      <AirdropNftHoldersCard />
      <AirdropSocialMediaCard />
      <AirdropMainnetCard />
    </SimpleGrid>
  )
}

const AirdropTestnetCard = () => {
  const { t } = useTranslation()
  const { onError } = useAppToast()
  const [userReward, setUserReward] = useState<null | number>(null)
  const [isEligible, setEligible] = useState<null | boolean>(null)
  const { account, signIn } = useSession()

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const result = await fetch(`${API_GATEWAY}/airdrop/${account?.injectiveAddress}`).then((res) => res.json())
      return result.testnet
    },
    onSuccess: (data) => {
      if (data && !isNaN(parseInt(data))) {
        setUserReward(parseInt(data))
        setEligible(true)
        return
      }

      setEligible(false)
    },
    onError: () => {
      onError()
    },
  })

  return (
    <Box bg={'ninja.500'} rounded={'2xl'}>
      <Box p={{ base: 3, md: 4 }}>
        <Image w={'full'} src={AirdropTestnet} alt={''} />
        <Text my={{ base: 3, md: 4 }} fontWeight={700} fontSize={{ base: 'lg', md: 'xl' }}>
          {t('airdrop.testnet-participants')}
        </Text>
        {isEligible && (
          <Flex gap={2}>
            <Flex
              flexBasis={'full'}
              alignItems={'center'}
              justifyContent={'center'}
              gap={2}
              h={'46px'}
              rounded={'2xl'}
              border={'1px solid'}
              borderColor={'ninja.400'}
            >
              <Box bg={'brand.green'} rounded={'full'} w={3} h={3} />
              <Text fontSize={'sm'} fontWeight={700}>
                {t('airdrop.eligible')}
              </Text>
            </Flex>
            <Flex
              gap={1}
              bg={'ninja.600'}
              alignItems={'center'}
              justifyContent={'center'}
              rounded={'2xl'}
              h={'46px'}
              flexShrink={0}
              px={4}
            >
              <ChestIcon />
              <Text fontSize={'sm'} fontWeight={700}>
                {userReward}
              </Text>
            </Flex>
          </Flex>
        )}
        {isEligible === false && (
          <Flex gap={2}>
            <Flex
              flexBasis={'full'}
              alignItems={'center'}
              justifyContent={'center'}
              gap={2}
              h={'46px'}
              rounded={'2xl'}
              border={'1px solid'}
              borderColor={'ninja.400'}
            >
              <Box bg={'brand.error'} rounded={'full'} w={3} h={3} />
              <Text fontSize={'sm'} fontWeight={700}>
                {t('airdrop.not-eligible')}
              </Text>
            </Flex>
          </Flex>
        )}
        {isEligible === null && (
          <Button
            isLoading={isPending}
            onClick={() => {
              if (!account?.injectiveAddress) {
                signIn()
                return
              }
              mutate()
            }}
            rounded={'2xl'}
            w={'full'}
          >
            {t('common.check')}
          </Button>
        )}
      </Box>
      <Box p={4} pt={3} borderTop={'1px solid'} borderColor={'ninja.400'}>
        <Text mb={2} fontWeight={700}>
          {t('airdrop.about-program')}
        </Text>
        <Text fontSize={'sm'} color={'ninja.100'}>
          {t('airdrop.testnet-description')}
        </Text>
      </Box>
    </Box>
  )
}

const AirdropNftHoldersCard = () => {
  const { t } = useTranslation()

  return (
    <Box pos={'relative'}>
      <Box
        pos={'absolute'}
        bg={'#5048A3'}
        px={2}
        py={1}
        rounded={'xl'}
        left={6}
        top={6}
        fontSize={'xs'}
        fontWeight={700}
        zIndex={10}
      >
        {t('common.coming-soon')}
      </Box>

      <Box bg={'ninja.500'} rounded={'2xl'} opacity={0.4}>
        <Box p={{ base: 3, md: 4 }}>
          <Image src={AirdropNftHolders} alt={''} />
          <Text my={{ base: 3, md: 4 }} fontWeight={700} fontSize={{ base: 'lg', md: 'xl' }}>
            {t('airdrop.nft-holders')}
          </Text>
          <Button isDisabled={true} rounded={'2xl'} w={'full'}>
            {t('common.check')}
          </Button>
        </Box>
        <Box p={4} pt={3} borderTop={'1px solid'} borderColor={'ninja.400'}>
          <Text mb={2} fontWeight={700}>
            {t('airdrop.about-program')}
          </Text>
          <Text fontSize={'sm'} color={'ninja.100'}>
            {t('airdrop.nft-description')}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}

const AirdropMainnetCard = () => {
  const { t } = useTranslation()

  return (
    <Box pos={'relative'}>
      <Box
        pos={'absolute'}
        bg={'#5048A3'}
        px={2}
        py={1}
        rounded={'xl'}
        left={6}
        top={6}
        fontSize={'xs'}
        fontWeight={700}
        zIndex={10}
      >
        {t('common.coming-soon')}
      </Box>

      <Box bg={'ninja.500'} rounded={'2xl'} opacity={0.4}>
        <Box p={{ base: 3, md: 4 }}>
          <Image src={AirdropMainnet} alt={''} />
          <Text my={{ base: 3, md: 4 }} fontWeight={700} fontSize={{ base: 'lg', md: 'xl' }}>
            {t('airdrop.mainnet-incentives')}
          </Text>
          <Button isDisabled={true} rounded={'2xl'} w={'full'}>
            {t('common.check')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

const AirdropSocialMediaCard = () => {
  const { t } = useTranslation()

  return (
    <Box pos={'relative'}>
      <Box
        pos={'absolute'}
        bg={'#5048A3'}
        px={2}
        py={1}
        rounded={'xl'}
        left={6}
        top={6}
        fontSize={'xs'}
        fontWeight={700}
        zIndex={10}
      >
        {t('common.coming-soon')}
      </Box>
      <Box bg={'ninja.500'} rounded={'2xl'} opacity={0.4}>
        <Box p={{ base: 3, md: 4 }}>
          <Image src={AirdropSocial} alt={''} />
          <Text my={{ base: 3, md: 4 }} fontWeight={700} fontSize={{ base: 'lg', md: 'xl' }}>
            {t('airdrop.community-rewards')}
          </Text>
          <Button isDisabled={true} rounded={'2xl'} w={'full'}>
            {t('common.check')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
